import React, { useState } from "react";
import {
  Container,
  TextField,
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  Visibility,
  VisibilityOff,
  Lock,
  Email,
  Business,
  LocationOn,
  Phone,
  WhatsApp,
  LocationCity,
  Public,
  Person,
  AccountBalance,
} from "@mui/icons-material";
import * as Yup from "yup";

import footer from "../utils/footer";
import { checkEmail, register } from "../services/auth";
import { useNavigate } from "react-router-dom";
import AppButton from "../utils/Appbutton";

// Validation schema
const validationSchema = Yup.object({
  nome: Yup.string().required("Nome é obrigatório"),
  email: Yup.string().email("Email inválido").required("Email é obrigatório"),
  password: Yup.string()
    .min(6, "Senha deve ter no mínimo 6 caracteres")
    .required("Senha é obrigatória"),
  retypePassword: Yup.string()
    .oneOf([Yup.ref("password")], "Senhas não coincidem")
    .required("Confirmação de senha é obrigatória"),
  empresa: Yup.string().required("Nome da empresa é obrigatório"),
  nif: Yup.string()
    .matches(/^\d{9}$/, "NIF deve ter 9 dígitos")
    .required("NIF é obrigatório"),
  morada: Yup.string().required("Morada é obrigatória"),
  telemovel: Yup.string()
    .matches(/^\d{9}$/, "Telemóvel deve ter 9 dígitos")
    .required("Telemóvel é obrigatório"),
  whatsapp: Yup.string()
    .matches(/^\d{9}$/, "WhatsApp deve ter 9 dígitos")
    .required("WhatsApp é obrigatório"),
  cidade: Yup.string().required("Cidade é obrigatória"),
  pais: Yup.string().required("País é obrigatório"),
});

const RegisterScreen = () => {
  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    password: "",
    retypePassword: "",
    empresa: "",
    nif: "",
    morada: "",
    telemovel: "",
    whatsapp: "",
    cidade: "",
    pais: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  const countries = [
    "Portugal",
    "Espanha",
    "França",
    "Itália",
    "Alemanha",
    "Reino Unido",
    "Estados Unidos",
    "Brasil",
    "Angola",
    "Moçambique",
    "Cabo Verde",
    "São Tomé e Príncipe",
    "Guiné-Bissau",
    "Timor-Leste",
  ];

  // const validateField = async (name, value) => {
  //   try {
  //     await validationSchema.validateAt(name, { [name]: value });
  //     setErrors((prev) => ({ ...prev, [name]: "" }));
  //   } catch (err) {
  //     setErrors((prev) => ({ ...prev, [name]: err.message }));
  //   }
  // };

  const handleChange = (e) => {
    const { name, value } = e.target;

    const updated = { ...formData, [name]: value };
    setFormData(updated);

    if (name === "password" || name === "retypePassword") {
      validationSchema
        .validateAt("retypePassword", updated)
        .then(() => {
          setErrors((prev) => ({ ...prev, retypePassword: "" }));
        })
        .catch((err) => {
          setErrors((prev) => ({ ...prev, retypePassword: err.message }));
        });
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;

    setTouched((prev) => ({ ...prev, [name]: true }));

    validationSchema
      .validateAt(name, formData)
      .then(() => {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      })
      .catch((err) => {
        setErrors((prev) => ({ ...prev, [name]: err.message }));
      });
  };

  const handleEmailBlur = async (e) => {
    const { value } = e.target;

    setTouched((prev) => ({ ...prev, email: true }));

    let validationError = "";

    try {
      await validationSchema.validateAt("email", { email: value });
    } catch (err) {
      validationError = err.message;
    }

    setErrors((prev) => ({
      ...prev,
      email: validationError,
    }));

    if (validationError) return;

    try {
      const data = await checkEmail({ email: value });

      setErrors((prev) => ({
        ...prev,
        email: data.exists ? "Email já está registado" : "",
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      await validationSchema.validate(formData, { abortEarly: false });

      setLoading(true);

      const emailCheck = await checkEmail({ email: formData.email });

      if (emailCheck.exists) {
        setTouched((prev) => ({ ...prev, email: true }));
        setErrors((prev) => ({
          ...prev,
          email: "Email já está registado",
        }));
        return;
      }

      const response = await register(formData);

      // check backend success
      if (response?.message === "true") {
        setSuccessMessage("Empresa Criada Com Sucesso!");
        setSuccessOpen(true);

        // reset form
        setFormData({
          nome: "",
          email: "",
          password: "",
          retypePassword: "",
          empresa: "",
          nif: "",
          morada: "",
          telemovel: "",
          whatsapp: "",
          cidade: "",
          pais: "",
        });

        setErrors({});
        setTouched({});
      } else {
        setError("Erro ao criar conta");
      }
    } catch (err) {
      if (err.inner) {
        const newErrors = {};
        err.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });
        setErrors(newErrors);
      }

      setError("Por favor, verifique os dados informados");
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowRetypePassword = () => {
    setShowRetypePassword(!showRetypePassword);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
      }}
    >
      <Dialog
        open={successOpen}
        onClose={() => {
          setSuccessOpen(false);
          navigate("/"); // login page
        }}
      >
        <DialogTitle>{successMessage}</DialogTitle>

        <DialogContent>
          <Typography>Pode fazer login agora</Typography>
        </DialogContent>

        <DialogActions>
          <AppButton
            minWidth="100px"
            type="button"
            valueText="Ok"
            loading={false}
            size="small"
            onClick={() => {
              setSuccessOpen(false);
              navigate("/"); // redirect to login
            }}
          />
        </DialogActions>
      </Dialog>
      <Container maxWidth="md">
        <Card
          elevation={6}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #9c27b0 0%, #757575 100%)",
              color: "white",
              padding: 3,
              textAlign: "center",
            }}
          >
            <Business
              sx={{
                fontSize: 48,
                marginBottom: 1,
              }}
            />
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", marginBottom: 1 }}
            >
              Criar Conta Empresa
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Registe-se para acessar o sistema de gestão de documentos
            </Typography>
          </Box>

          <CardContent sx={{ padding: 4 }}>
            {error && (
              <Alert severity="error" sx={{ marginBottom: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3} sx={{ width: "100%" }}>
                {/* Nome */}
                <TextField
                  fullWidth
                  id="nome"
                  name="nome"
                  label="Nome Completo"
                  type="nome"
                  placeholder="Nome do responsável pela empresa"
                  variant="filled"
                  value={formData.nome}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.nome && Boolean(errors.nome)}
                  helperText={touched.nome && errors.nome}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                {/* Email */}
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="seu.email@exemplo.com"
                  variant="filled"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleEmailBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Nome Empresa */}

                <TextField
                  fullWidth
                  id="empresa"
                  name="empresa"
                  label="Nome da Empresa"
                  placeholder="Nome da sua empresa"
                  variant="filled"
                  value={formData.empresa}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.empresa && Boolean(errors.empresa)}
                  helperText={touched.empresa && errors.empresa}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiFilledInput-root": {
                      backgroundColor: "#fafafa",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                />

                {/* Password */}
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    label="Senha"
                    type={showPassword ? "text" : "password"}
                    placeholder="Digite sua senha"
                    variant="filled"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: "#9c27b0" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <IconButton
                    onClick={handleClickShowPassword}
                    sx={{
                      position: "absolute",
                      right: 30,
                      top: "50%",
                      transform: "translateY(-50%)",
                      padding: 0.5,
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Box>

                {/* Retype Password */}
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    id="retypePassword"
                    name="retypePassword"
                    label="Confirmar Senha"
                    type={showRetypePassword ? "text" : "password"}
                    placeholder="Confirme sua senha"
                    variant="filled"
                    value={formData.retypePassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: "#9c27b0" }} />
                        </InputAdornment>
                      ),
                    }}
                    error={
                      touched.retypePassword && Boolean(errors.retypePassword)
                    }
                    helperText={touched.retypePassword && errors.retypePassword}
                  />
                  <IconButton
                    onClick={handleClickShowRetypePassword}
                    sx={{
                      position: "absolute",
                      right: 30,
                      top: "50%",
                      transform: "translateY(-50%)",
                      padding: 0.5,
                    }}
                  >
                    {showRetypePassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Box>

                {/* NIF */}
                <TextField
                  fullWidth
                  id="nif"
                  name="nif"
                  label="NIF"
                  placeholder="123456789"
                  variant="filled"
                  value={formData.nif}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.nif && Boolean(errors.nif)}
                  helperText={touched.nif && errors.nif}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalance sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                />

                {/* Telemovel */}
                <TextField
                  fullWidth
                  id="telemovel"
                  name="telemovel"
                  label="Telemóvel"
                  placeholder="912345678"
                  variant="filled"
                  value={formData.telemovel}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.telemovel && Boolean(errors.telemovel)}
                  helperText={touched.telemovel && errors.telemovel}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                />

                {/* Whatsapp */}
                <TextField
                  fullWidth
                  id="whatsapp"
                  name="whatsapp"
                  label="WhatsApp"
                  placeholder="912345678"
                  variant="filled"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.whatsapp && Boolean(errors.whatsapp)}
                  helperText={touched.whatsapp && errors.whatsapp}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsApp sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                />

                {/* Morada */}
                <TextField
                  fullWidth
                  id="morada"
                  name="morada"
                  label="Morada"
                  placeholder="Rua, número, andar, etc."
                  variant="filled"
                  value={formData.morada}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.morada && Boolean(errors.morada)}
                  helperText={touched.morada && errors.morada}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                />

                {/* Pais */}
                <TextField
                  fullWidth
                  select
                  id="pais"
                  name="pais"
                  label="País"
                  variant="filled"
                  value={formData.pais}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.pais && Boolean(errors.pais)}
                  helperText={touched.pais && errors.pais}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Public sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                >
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Cidade */}
                <TextField
                  fullWidth
                  id="cidade"
                  name="cidade"
                  label="Cidade"
                  placeholder="Nome da cidade"
                  variant="filled"
                  value={formData.cidade}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.cidade && Boolean(errors.cidade)}
                  helperText={touched.cidade && errors.cidade}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCity sx={{ color: "#9c27b0" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                />

                {/* Register Button */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 2,
                  }}
                >
                  <AppButton
                    type="submit"
                    valueText="Criar Conta"
                    size="large"
                    loading={loading}
                    disabled={loading}
                  />
                </Box>

                {/* Login Link */}
                <Box
                  sx={{
                    textAlign: "center",
                    marginTop: 2,
                    paddingTop: 2,
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    Já tem uma conta?{" "}
                    <Link
                      to="/"
                      style={{
                        color: "#9c27b0",
                        fontWeight: "bold",
                        textDecoration: "none",
                      }}
                    >
                      Faça login aqui
                    </Link>
                  </Typography>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        {footer()}
      </Container>
    </Box>
  );
};

export default RegisterScreen;
